import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode } from "vue"
import _imports_0 from '@/assets/images/france.png'
import _imports_1 from '@/assets/images/royaume-uni.png'
import _imports_2 from '@/assets/images/espagne.png'
import _imports_3 from '@/assets/images/italie.png'


const _hoisted_1 = { class: "space-y-4" }
const _hoisted_2 = {
  key: 0,
  class: "flex items-center justify-between"
}
const _hoisted_3 = { class: "text-sm font-medium text-gray-700" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = {
  key: 0,
  class: "animate-spin h-3.5 w-3.5",
  xmlns: "http://www.w3.org/2000/svg",
  fill: "none",
  viewBox: "0 0 24 24"
}
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { class: "space-y-3" }
const _hoisted_8 = { class: "flex items-start gap-3" }
const _hoisted_9 = ["value", "rows", "placeholder", "required"]
const _hoisted_10 = { class: "flex items-start gap-3" }
const _hoisted_11 = ["value", "rows", "placeholder"]
const _hoisted_12 = { class: "flex items-start gap-3" }
const _hoisted_13 = ["value", "rows", "placeholder"]
const _hoisted_14 = { class: "flex items-start gap-3" }
const _hoisted_15 = ["value", "rows", "placeholder"]

import { ref } from 'vue'
import { fetchRest, getApiBaseUrl } from '@/config/api'

interface MultilingualText {
  francais: string
  anglais: string
  espagnol: string
  italien: string
}

interface Props {
  modelValue: MultilingualText
  title?: string
  rows?: number
  required?: boolean
  placeholders?: Partial<MultilingualText>
}

interface Emits {
  (e: 'update:modelValue', value: MultilingualText): void
}


export default /*@__PURE__*/_defineComponent({
  __name: 'MultilingualTextarea',
  props: {
    modelValue: {},
    title: {},
    rows: { default: 3 },
    required: { type: Boolean, default: false },
    placeholders: { default: () => ({}) }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props

const emit = __emit
const isTranslating = ref(false)

const updateValue = (lang: keyof MultilingualText, value: string) => {
  emit('update:modelValue', {
    ...props.modelValue,
    [lang]: value
  })
}

const ALL_LANGS: (keyof MultilingualText)[] = ['francais', 'anglais', 'espagnol', 'italien']

async function autoTranslate() {
  // 找到第一个有内容的语言作为源语言
  const sourceLang = ALL_LANGS.find(lang => props.modelValue[lang]?.trim())

  if (!sourceLang) {
    alert('Veuillez remplir au moins une description dans une langue.')
    return
  }

  const text = props.modelValue[sourceLang].trim()
  const targetLangs = ALL_LANGS.filter(lang => lang !== sourceLang)

  isTranslating.value = true
  try {
    const API_URL = getApiBaseUrl()
    const response = await fetchRest(`${API_URL}/translate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, sourceLang, targetLangs }),
    })

    if (!response.ok) {
      throw new Error('Translation request failed')
    }

    const data = await response.json()

    // 更新所有翻译结果
    const updated = { ...props.modelValue }
    for (const lang of targetLangs) {
      if (data[lang]) {
        updated[lang as keyof MultilingualText] = data[lang]
      }
    }
    emit('update:modelValue', updated)
  } catch (err) {
    console.error('Translation error:', err)
    alert('Erreur lors de la traduction. Veuillez réessayer.')
  } finally {
    isTranslating.value = false
  }
}

// Indique si une traduction de secours est nécessaire :
// au moins une langue remplie mais au moins une autre vide.
function needsAutoTranslate(): boolean {
  const filled = ALL_LANGS.filter(lang => props.modelValue[lang]?.trim())
  return filled.length > 0 && filled.length < ALL_LANGS.length
}

// Exposé au parent pour permettre la traduction automatique à l'enregistrement
// lorsque l'utilisateur n'a pas cliqué sur "AI Traduction".
__expose({ autoTranslate, needsAutoTranslate })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_ctx.title)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("h4", _hoisted_3, _toDisplayString(_ctx.title), 1),
          _createElementVNode("button", {
            type: "button",
            onClick: autoTranslate,
            disabled: isTranslating.value,
            class: "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          }, [
            (isTranslating.value)
              ? (_openBlock(), _createElementBlock("svg", _hoisted_5, _cache[4] || (_cache[4] = [
                  _createElementVNode("circle", {
                    class: "opacity-25",
                    cx: "12",
                    cy: "12",
                    r: "10",
                    stroke: "currentColor",
                    "stroke-width": "4"
                  }, null, -1),
                  _createElementVNode("path", {
                    class: "opacity-75",
                    fill: "currentColor",
                    d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  }, null, -1)
                ])))
              : (_openBlock(), _createElementBlock("span", _hoisted_6, "🤖")),
            _createTextVNode(" " + _toDisplayString(isTranslating.value ? 'Translating...' : 'AI Traduction'), 1)
          ], 8, _hoisted_4)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_7, [
      _createElementVNode("div", _hoisted_8, [
        _cache[5] || (_cache[5] = _createElementVNode("img", {
          src: _imports_0,
          alt: "Français",
          class: "w-6 h-4 mt-2"
        }, null, -1)),
        _createElementVNode("textarea", {
          value: _ctx.modelValue.francais,
          onInput: _cache[0] || (_cache[0] = ($event: any) => (updateValue('francais', ($event.target as HTMLTextAreaElement).value))),
          rows: _ctx.rows,
          placeholder: _ctx.placeholders.francais || 'Description en français...',
          required: _ctx.required,
          class: "flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
        }, null, 40, _hoisted_9)
      ]),
      _createElementVNode("div", _hoisted_10, [
        _cache[6] || (_cache[6] = _createElementVNode("img", {
          src: _imports_1,
          alt: "Anglais",
          class: "w-6 h-4 mt-2"
        }, null, -1)),
        _createElementVNode("textarea", {
          value: _ctx.modelValue.anglais,
          onInput: _cache[1] || (_cache[1] = ($event: any) => (updateValue('anglais', ($event.target as HTMLTextAreaElement).value))),
          rows: _ctx.rows,
          placeholder: _ctx.placeholders.anglais || 'Description en anglais...',
          class: "flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
        }, null, 40, _hoisted_11)
      ]),
      _createElementVNode("div", _hoisted_12, [
        _cache[7] || (_cache[7] = _createElementVNode("img", {
          src: _imports_2,
          alt: "Espagnol",
          class: "w-6 h-4 mt-2"
        }, null, -1)),
        _createElementVNode("textarea", {
          value: _ctx.modelValue.espagnol,
          onInput: _cache[2] || (_cache[2] = ($event: any) => (updateValue('espagnol', ($event.target as HTMLTextAreaElement).value))),
          rows: _ctx.rows,
          placeholder: _ctx.placeholders.espagnol || 'Description en espagnol...',
          class: "flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
        }, null, 40, _hoisted_13)
      ]),
      _createElementVNode("div", _hoisted_14, [
        _cache[8] || (_cache[8] = _createElementVNode("img", {
          src: _imports_3,
          alt: "Italien",
          class: "w-6 h-4 mt-2"
        }, null, -1)),
        _createElementVNode("textarea", {
          value: _ctx.modelValue.italien,
          onInput: _cache[3] || (_cache[3] = ($event: any) => (updateValue('italien', ($event.target as HTMLTextAreaElement).value))),
          rows: _ctx.rows,
          placeholder: _ctx.placeholders.italien || 'Description en italien...',
          class: "flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
        }, null, 40, _hoisted_15)
      ])
    ])
  ]))
}
}

})